/**
 * HexKey Component - Individual hexagonal key visualization
 * Spec §3.1: Flat-top hexagon geometry
 */

import { memo, useEffect, useRef } from 'react';
import type { HexCoord } from '@/types/hex';

/** Override display info passed from parent */
export interface OverrideDisplayInfo {
  type: string; // 'note', 'cc', 'chord', 'program_change', etc.
  label: string; // Short label like "Ch" for chord, "CC" for control change
  detail?: string; // Additional detail like note name or CC number
}

/** Microtonal display info for non-12-EDO tunings */
export interface MicrotonalInfo {
  stepLabel: string;  // e.g. "5\19"
  centsLabel: string; // e.g. "+632c"
}

export interface HexKeyProps {
  coord: HexCoord;
  note: number;
  noteName: string;
  x: number; // Center X position (from hexToPixel)
  y: number; // Center Y position (from hexToPixel)
  size: number; // Hexagon size (radius)
  ledColor?: string; // LED color as CSS color (e.g., "rgb(30, 30, 30)")
  isSelected?: boolean; // Whether this key is currently selected
  hasOverride?: boolean; // Whether this key has a MIDI override
  hasLEDOverride?: boolean; // Whether this key has an LED override
  overrideInfo?: OverrideDisplayInfo | undefined; // Override details to display
  microtonalInfo?: MicrotonalInfo | undefined; // Microtonal display labels (replaces note/name when present)
  isPressed?: boolean; // Live key press state
  pressVelocity?: number; // 0-127 velocity for glow intensity
  onClick?: (coord: HexCoord) => void;
  onHoverEnter?: (coord: HexCoord) => void;
  onHoverLeave?: () => void;
}

/**
 * Generate SVG path for flat-top hexagon
 * Spec §3.1: Keys are flat-top hexagons
 *
 * @param cx - Center X
 * @param cy - Center Y
 * @param size - Radius (center to corner)
 * @returns SVG path string
 */
function generateHexPath(cx: number, cy: number, size: number): string {
  // Flat-top hexagon: 6 corners with flat sides at top and bottom
  // Start from right corner, go clockwise
  // Angles offset by 30° from pointy-top: 30°, 90°, 150°, 210°, 270°, 330°
  const angles = [30, 90, 150, 210, 270, 330];
  const points = angles.map((angle) => {
    const rad = (Math.PI / 180) * angle;
    const x = cx + size * Math.cos(rad);
    const y = cy + size * Math.sin(rad);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  return `M ${points.join(' L ')} Z`;
}

/**
 * HexKey - Renders a single hexagonal key
 * Shows global (q,r) coordinate and MIDI note
 */
export const HexKey = memo(function HexKey({
  coord,
  note,
  noteName,
  x,
  y,
  size,
  ledColor = '#f0f0f0',
  isSelected = false,
  hasOverride = false,
  hasLEDOverride = false,
  overrideInfo,
  microtonalInfo,
  isPressed = false,
  pressVelocity = 0,
  onClick,
  onHoverEnter,
  onHoverLeave,
}: HexKeyProps) {
  const path = generateHexPath(x, y, size);
  const pathRef = useRef<SVGPathElement>(null);

  // Sync visual state when ledColor changes (clear any style.fill overrides)
  useEffect(() => {
    if (pathRef.current) {
      // Clear any style.fill set by hover to ensure fill attribute takes effect
      pathRef.current.style.fill = '';
    }
  }, [ledColor]);

  const handleClick = () => {
    if (onClick) {
      onClick(coord);
    }
  };

  // Determine stroke color and width based on state
  const strokeColor = isSelected ? '#0066ff' : hasOverride ? '#ff6600' : hasLEDOverride ? '#00cc66' : '#333';
  const strokeWidth = isSelected ? 4 : hasOverride || hasLEDOverride ? 3 : 2;

  // Pressed key effects: glow + velocity-based fill brightening
  const glowIntensity = isPressed ? 0.3 + 0.7 * (pressVelocity / 127) : 0;
  const glowRadius = isPressed ? 3 + 5 * (pressVelocity / 127) : 0;

  // Blend fill toward white based on velocity (simulates LED brightening)
  let fillColor = ledColor;
  if (isPressed) {
    const match = ledColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      const t = pressVelocity / 127;
      const r = Math.round(Number(match[1]) + (255 - Number(match[1])) * t);
      const g = Math.round(Number(match[2]) + (255 - Number(match[2])) * t);
      const b = Math.round(Number(match[3]) + (255 - Number(match[3])) * t);
      fillColor = `rgb(${r}, ${g}, ${b})`;
    }
  }

  return (
    <g
      onClick={handleClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      data-q={coord.q}
      data-r={coord.r}
    >
      {/* Hexagon shape with LED color */}
      <path
        ref={pathRef}
        d={path}
        fill={fillColor}
        stroke={isPressed ? `rgb(255, ${Math.round(220 - 180 * (pressVelocity / 127))}, ${Math.round(60 - 60 * (pressVelocity / 127))})` : strokeColor}
        strokeWidth={isPressed ? 3 : strokeWidth}
        style={{
          transition: 'fill 0.2s, stroke 0.15s, stroke-width 0.15s',
          ...(isPressed ? {
            filter: `drop-shadow(0 0 ${glowRadius}px rgba(255, ${Math.round(220 - 180 * (pressVelocity / 127))}, ${Math.round(60 - 60 * (pressVelocity / 127))}, ${glowIntensity}))`,
          } : {}),
        }}
        onMouseEnter={(e) => {
          if (!isPressed) {
            e.currentTarget.style.fill = '#e0e0e0';
          }
          onHoverEnter?.(coord);
        }}
        onMouseLeave={(e) => {
          if (!isPressed) {
            e.currentTarget.style.fill = '';
          }
          onHoverLeave?.();
        }}
      />

      {/* Coordinate label (q,r) - small debug text at top */}
      <text
        x={x}
        y={y - 12}
        textAnchor="middle"
        fontSize="7"
        fill="#666"
        stroke="#fff"
        strokeWidth="2"
        paintOrder="stroke"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {coord.q},{coord.r}
      </text>

      {/* Main content depends on whether there's an override */}
      {overrideInfo ? (
        <>
          {/* Override type label */}
          <text
            x={x}
            y={y - 2}
            textAnchor="middle"
            fontSize="9"
            fontWeight="bold"
            fill="#ff6600"
            stroke="#fff"
            strokeWidth="2"
            paintOrder="stroke"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {overrideInfo.label}
          </text>
          {/* Override detail */}
          <text
            x={x}
            y={y + 8}
            textAnchor="middle"
            fontSize="8"
            fill="#333"
            stroke="#fff"
            strokeWidth="2"
            paintOrder="stroke"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {overrideInfo.detail}
          </text>
          {/* Original note (tiny, for reference) */}
          <text
            x={x}
            y={y + 17}
            textAnchor="middle"
            fontSize="6"
            fill="#666"
            stroke="#fff"
            strokeWidth="2"
            paintOrder="stroke"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            ({note})
          </text>
        </>
      ) : microtonalInfo ? (
        <>
          {/* Microtonal step label (e.g. "5\19") */}
          <text
            x={x}
            y={y + 1}
            textAnchor="middle"
            fontSize="10"
            fontWeight="bold"
            fill="#000"
            stroke="#fff"
            strokeWidth="2.5"
            paintOrder="stroke"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {microtonalInfo.stepLabel}
          </text>
          {/* Cent offset label (e.g. "+632c") */}
          <text
            x={x}
            y={y + 12}
            textAnchor="middle"
            fontSize="8"
            fill="#333"
            stroke="#fff"
            strokeWidth="2"
            paintOrder="stroke"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {microtonalInfo.centsLabel}
          </text>
        </>
      ) : (
        <>
          {/* Note number */}
          <text
            x={x}
            y={y + 1}
            textAnchor="middle"
            fontSize="10"
            fontWeight="bold"
            fill="#000"
            stroke="#fff"
            strokeWidth="2.5"
            paintOrder="stroke"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {note}
          </text>
          {/* Note name */}
          <text
            x={x}
            y={y + 12}
            textAnchor="middle"
            fontSize="8"
            fill="#333"
            stroke="#fff"
            strokeWidth="2"
            paintOrder="stroke"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {noteName}
          </text>
        </>
      )}
    </g>
  );
});
